import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './TrabalheConosco.css';
import { SiteHeader, SiteFooter } from '../components/Shared';
import { getStaffCompleteInfo, getStaffCities, submitStaffComplete } from '../api';

const UF_NAMES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
  DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão',
  MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará',
  PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima',
  SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
};

function isValidCPF(cpf) {
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  for (let t = 9; t < 11; t++) {
    let sum = 0;
    for (let i = 0; i < t; i++) sum += parseInt(cpf.charAt(i), 10) * ((t + 1) - i);
    const digit = ((sum * 10) % 11) % 10;
    if (parseInt(cpf.charAt(t), 10) !== digit) return false;
  }

  return true;
}

const initialForm = {
  birthday: '', cpf: '', rg: '', nis_pasep: '',
  zip: '', address: '', number: '', complement: '', neighborhood: '', state: '', city: '',
  document_type: 'rg',
  bank: '', branch: '', account: '', account_type: 'corrente',
  pix_key_type: 'cpf', pix_key: '',
};

function TrabalheConoscoCompletar() {
  const { candidate, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [documentFile, setDocumentFile] = useState(null);
  const [cities, setCities] = useState([]);
  const [zipHelp, setZipHelp] = useState('Preencha o CEP para completar o endereço automaticamente.');
  const [cpfHelp, setCpfHelp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getStaffCompleteInfo(candidate, token)
      .then(setInfo)
      .finally(() => setLoading(false));
  }, [candidate, token]);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleStateChange(stateId) {
    setField('state', stateId);
    setField('city', '');
    setCities([]);

    if (!stateId) return;

    const list = await getStaffCities(candidate, token, stateId);
    setCities(Array.isArray(list) ? list : []);
  }

  async function handleZipBlur() {
    const zip = form.zip.replace(/\D/g, '');
    setZipHelp('Preencha o CEP para completar o endereço automaticamente.');

    if (zip.length !== 8) return;

    setZipHelp('Buscando endereço...');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${zip}/json`);
      const data = await response.json();

      if (data.erro) {
        setZipHelp('CEP não encontrado — preencha o endereço manualmente.');
        return;
      }

      setZipHelp('');
      setField('address', data.logradouro || '');
      setField('neighborhood', data.bairro || '');

      const stateName = UF_NAMES[data.uf];
      const matchedState = stateName && info.states.find((s) => s.name === stateName);

      if (matchedState) {
        setField('state', matchedState.id);
        const list = await getStaffCities(candidate, token, matchedState.id);
        setCities(Array.isArray(list) ? list : []);

        const matchedCity = list.find((c) => c.name === data.localidade);
        if (matchedCity) setField('city', matchedCity.id);
      }
    } catch (e) {
      setZipHelp('Não foi possível buscar o CEP — preencha o endereço manualmente.');
    }
  }

  function handleCpfBlur() {
    setCpfHelp(form.cpf && !isValidCPF(form.cpf) ? 'CPF inválido.' : '');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = [];
    const required = {
      birthday: 'Data de nascimento', cpf: 'CPF', rg: 'RG',
      zip: 'CEP', address: 'Endereço', number: 'Número',
      neighborhood: 'Bairro', state: 'Estado', city: 'Cidade',
      bank: 'Banco', branch: 'Agência', account: 'Conta',
    };

    Object.entries(required).forEach(([field, label]) => {
      if (!form[field]) errors.push(`Preencha o campo "${label}".`);
    });

    if (!documentFile) errors.push('Envie o documento.');
    if (form.cpf && !isValidCPF(form.cpf)) errors.push('CPF inválido.');
    if (form.pix_key_type === 'aleatoria' && !form.pix_key) errors.push('Preencha o campo "Chave Pix".');

    if (errors.length > 0) {
      setResult({ status: 'error', errors });
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    formData.append('userfile', documentFile);

    setSubmitting(true);
    setResult(null);

    try {
      const response = await submitStaffComplete(candidate, token, formData);
      setResult(response);
    } catch (e) {
      setResult({ status: 'error', errors: ['Não foi possível enviar. Tente novamente.'] });
    } finally {
      setSubmitting(false);
    }
  }

  const status = info ? info.status : (loading ? null : 'invalid');
  const resultStatus = result ? result.status : null;
  const errors = result && result.status === 'error' ? result.errors || [] : [];
  const showPixKey = form.pix_key_type === 'aleatoria';

  return (
    <div className="App">
      <SiteHeader />

      <main id="conteudo" className="tc">
        <div className="tc__shell tc__shell--narrow">
          <section className="tc__intro">
            <span className="tc__eyebrow">Falta pouco</span>
            <h1 className="tc__title">Complete seu cadastro</h1>
            <p className="tc__lead">
              Preencha o restante dos seus dados e envie um documento para a nossa equipe conferir.
            </p>
          </section>

          {loading && <p className="tc__copy">Carregando...</p>}

          {!loading && status === 'invalid' && (
            <div className="tc__alert tc__alert--error" role="alert">
              <span className="tc__alert__title">Link inválido ou expirado.</span>
              <p>Peça pra nossa equipe gerar um novo link pra você.</p>
            </div>
          )}

          {!loading && status === 'pendent' && (
            <div className="tc__alert tc__alert--info" role="status">
              <span className="tc__alert__title">Seus dados e documento já foram enviados.</span>
              <p>Não é necessário preencher o formulário novamente. Nossa equipe confere seus dados e o documento, e você recebe um aviso quando o cadastro for aprovado.</p>
            </div>
          )}

          {!loading && status === 'active' && (
            <div className="tc__alert tc__alert--info" role="status">
              <span className="tc__alert__title">Seu cadastro já está aprovado!</span>
              <p>Não é necessário preencher este formulário novamente. Fique de olho no WhatsApp/e-mail: é por lá que avisamos a disponibilidade das próximas produções.</p>
            </div>
          )}

          {!loading && status === 'ok' && resultStatus === 'success' && (
            <div className="tc__alert tc__alert--success" role="status">
              <span className="tc__alert__title">Cadastro enviado com sucesso!</span>
              <p>Seus dados e documento serão conferidos pela nossa equipe. Assim que aprovado, você começa a receber os avisos de disponibilidade das produções.</p>
            </div>
          )}

          {!loading && status === 'ok' && resultStatus !== 'success' && (
            <section className="tc__form-panel tc__form-panel--standalone">
              {errors.length > 0 && (
                <div className="tc__alert tc__alert--error" role="alert">
                  <span className="tc__alert__title">Não foi possível enviar.</span>
                  <p>{errors.join(' ')}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="tc__step">
                  <span className="tc__step__number">1</span>
                  <h2 className="tc__step__title">Dados pessoais</h2>
                </div>

                <div className="tc__row">
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="birthday">Data de nascimento <span className="tc__required">*</span></label>
                    <input className="tc__input" id="birthday" type="date" required
                      value={form.birthday} onChange={(e) => setField('birthday', e.target.value)} />
                  </div>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="cpf">CPF <span className="tc__required">*</span></label>
                    <input className="tc__input" id="cpf" type="text" inputMode="numeric" maxLength={14} required
                      value={form.cpf} onChange={(e) => setField('cpf', e.target.value)} onBlur={handleCpfBlur} />
                    <small className="tc__help">{cpfHelp}</small>
                  </div>
                </div>

                <div className="tc__row">
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="rg">RG <span className="tc__required">*</span></label>
                    <input className="tc__input" id="rg" type="text" required
                      value={form.rg} onChange={(e) => setField('rg', e.target.value)} />
                  </div>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="nis_pasep">NIS/PASEP</label>
                    <input className="tc__input" id="nis_pasep" type="text"
                      value={form.nis_pasep} onChange={(e) => setField('nis_pasep', e.target.value)} />
                  </div>
                </div>

                <div className="tc__step">
                  <span className="tc__step__number">2</span>
                  <h2 className="tc__step__title">Endereço</h2>
                </div>

                <div className="tc__field">
                  <label className="tc__label" htmlFor="zip">CEP <span className="tc__required">*</span></label>
                  <input className="tc__input" id="zip" type="text" inputMode="numeric" maxLength={9} required
                    value={form.zip} onChange={(e) => setField('zip', e.target.value)} onBlur={handleZipBlur} />
                  <small className="tc__help">{zipHelp}</small>
                </div>

                <div className="tc__field">
                  <label className="tc__label" htmlFor="address">Endereço <span className="tc__required">*</span></label>
                  <input className="tc__input" id="address" type="text" required
                    value={form.address} onChange={(e) => setField('address', e.target.value)} />
                </div>

                <div className="tc__row">
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="number">Número <span className="tc__required">*</span></label>
                    <input className="tc__input" id="number" type="text" required
                      value={form.number} onChange={(e) => setField('number', e.target.value)} />
                  </div>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="complement">Complemento</label>
                    <input className="tc__input" id="complement" type="text"
                      value={form.complement} onChange={(e) => setField('complement', e.target.value)} />
                  </div>
                </div>

                <div className="tc__field">
                  <label className="tc__label" htmlFor="neighborhood">Bairro <span className="tc__required">*</span></label>
                  <input className="tc__input" id="neighborhood" type="text" required
                    value={form.neighborhood} onChange={(e) => setField('neighborhood', e.target.value)} />
                </div>

                <div className="tc__row">
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="state">Estado <span className="tc__required">*</span></label>
                    <select className="tc__input" id="state" required
                      value={form.state} onChange={(e) => handleStateChange(e.target.value)}>
                      <option value="">Selecione</option>
                      {info && info.states.map((s) => (
                        <option value={s.id} key={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="city">Cidade <span className="tc__required">*</span></label>
                    <select className="tc__input" id="city" required
                      value={form.city} onChange={(e) => setField('city', e.target.value)}
                      disabled={cities.length === 0}>
                      <option value="">{cities.length === 0 ? 'Selecione o estado primeiro' : 'Selecione'}</option>
                      {cities.map((c) => (
                        <option value={c.id} key={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="tc__step">
                  <span className="tc__step__number">3</span>
                  <h2 className="tc__step__title">Documentação</h2>
                </div>

                <p className="tc__copy">Envie uma foto legível de um dos documentos abaixo:</p>

                <div className="tc__field">
                  <div className="tc__radio-group">
                    <label className="tc__radio">
                      <input type="radio" name="document_type" value="rg"
                        checked={form.document_type === 'rg'}
                        onChange={() => setField('document_type', 'rg')} />
                      <span>RG</span>
                    </label>
                    <label className="tc__radio">
                      <input type="radio" name="document_type" value="ctps"
                        checked={form.document_type === 'ctps'}
                        onChange={() => setField('document_type', 'ctps')} />
                      <span>Carteira de Trabalho</span>
                    </label>
                  </div>
                </div>

                <div className="tc__field">
                  <input className="tc__file" type="file" required
                    onChange={(e) => setDocumentFile(e.target.files[0] || null)} />
                </div>

                <div className="tc__step">
                  <span className="tc__step__number">4</span>
                  <h2 className="tc__step__title">Dados bancários</h2>
                </div>

                <div className="tc__note">
                  <strong>Atenção:</strong> os dados bancários abaixo precisam estar em nome de{' '}
                  <strong>{info && info.candidate_name}</strong> — não aceitamos conta de terceiros.
                </div>

                <div className="tc__field">
                  <label className="tc__label" htmlFor="bank">Banco <span className="tc__required">*</span></label>
                  <select className="tc__input" id="bank" required
                    value={form.bank} onChange={(e) => setField('bank', e.target.value)}>
                    <option value="">Selecione</option>
                    {info && info.banks.map((b) => (
                      <option value={b.code} key={b.code}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div className="tc__row">
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="branch">Agência <span className="tc__required">*</span></label>
                    <input className="tc__input" id="branch" type="text" required
                      value={form.branch} onChange={(e) => setField('branch', e.target.value)} />
                  </div>
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="account">Conta <span className="tc__required">*</span></label>
                    <input className="tc__input" id="account" type="text" required
                      value={form.account} onChange={(e) => setField('account', e.target.value)} />
                  </div>
                </div>

                <div className="tc__field">
                  <div className="tc__radio-group">
                    <label className="tc__radio">
                      <input type="radio" name="account_type" value="corrente"
                        checked={form.account_type === 'corrente'}
                        onChange={() => setField('account_type', 'corrente')} />
                      <span>Corrente</span>
                    </label>
                    <label className="tc__radio">
                      <input type="radio" name="account_type" value="poupanca"
                        checked={form.account_type === 'poupanca'}
                        onChange={() => setField('account_type', 'poupanca')} />
                      <span>Poupança</span>
                    </label>
                  </div>
                </div>

                <div className="tc__field">
                  <label className="tc__label" htmlFor="pix_key_type">Tipo de chave Pix <span className="tc__required">*</span></label>
                  <select className="tc__input" id="pix_key_type" required
                    value={form.pix_key_type} onChange={(e) => setField('pix_key_type', e.target.value)}>
                    <option value="cpf">CPF</option>
                    <option value="telefone">Telefone</option>
                    <option value="email">E-mail</option>
                    <option value="aleatoria">Chave aleatória</option>
                  </select>
                  <small className="tc__help">
                    CPF, telefone e e-mail já informados no cadastro são usados automaticamente como chave
                    — só é preciso digitar a chave se ela for do tipo aleatória.
                  </small>
                </div>

                {showPixKey && (
                  <div className="tc__field">
                    <label className="tc__label" htmlFor="pix_key">Chave Pix aleatória <span className="tc__required">*</span></label>
                    <input className="tc__input" id="pix_key" type="text" required={showPixKey}
                      value={form.pix_key} onChange={(e) => setField('pix_key', e.target.value)} />
                  </div>
                )}

                <button className="tc__button" type="submit" disabled={submitting}>
                  {submitting ? 'Enviando...' : 'Enviar cadastro'}
                </button>
              </form>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default TrabalheConoscoCompletar;
